import { Component, Input, Output, EventEmitter, forwardRef, ChangeDetectorRef, OnChanges, SimpleChanges, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

@Component({
  selector: 'app-custom-select',
  templateUrl: './custom-select.component.html',
  styleUrls: ['./custom-select.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CustomSelectComponent),
      multi: true
    }
  ]
})
export class CustomSelectComponent implements ControlValueAccessor, OnChanges, OnDestroy {
  @Input() label: string = '';
  @Input() placeholder: string = '';
  @Input() disabled: boolean = false;
  @Input() required: boolean = false;
  @Input() name: string = '';
  @Input() id: string = '';
  @Input() inputClass: string | string[] | Set<string> | { [klass: string]: any } = '';
  @Input() options: any[] = [];
  @Input() displayKey: string = 'name';
  @Input() valueKey: string = 'id';
  @Input() currentLanguage: string = 'ru';
  @Input() fieldSource: string = ''; // To identify gsvsCode

  @Output() selectChange = new EventEmitter<any>();
  @Output() openedChange = new EventEmitter<boolean>();
  @Output() closed = new EventEmitter<void>();
  @Output() searchChange = new EventEmitter<string>();
  @Output() scrollEnd = new EventEmitter<void>();

  private _value: any = null;
  searchTerm: string = '';
  filteredOptions: any[] = [];
  isFocused: boolean = false;
  private modalRef: any = null;
  private searchSubject = new Subject<string>();

  constructor(
    private modalService: NgbModal,
    private cdRef: ChangeDetectorRef
  ) {
    // Setup debounced search for gsvsCode
    this.searchSubject
      .pipe(
        debounceTime(300),
        distinctUntilChanged()
      )
      .subscribe(searchTerm => {
        this.searchChange.emit(searchTerm);
        if (this.fieldSource !== 'gsvsCode') {
          this.filterOptions();
        }
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Update filteredOptions when options change (e.g., from pagination)
    if (changes['options'] && !changes['options'].firstChange) {
      if (this.isFocused) {
        // If modal is open, update filtered options
        if (!this.searchTerm || this.searchTerm.trim() === '') {
          this.filteredOptions = [...this.options];
        } else if (this.fieldSource !== 'gsvsCode') {
          // For non-gsvsCode, filter locally
          this.filterOptions();
        } else {
          // For gsvsCode, use the new options directly (already filtered by backend)
          this.filteredOptions = [...this.options];
        }
        this.cdRef.detectChanges();
      }
    }
  }

  ngOnDestroy(): void {
    this.searchSubject.complete();
  }

  @Input()
  set value(val: any) {
    if (val !== this._value) {
      this._value = val;
      this.onChange(val);
    }
  }

  get value(): any {
    return this._value;
  }

  get hasValue(): boolean {
    return this.value !== null && this.value !== undefined && this.value !== '';
  }

  get displayValue(): string {
    if (!this.hasValue || !this.options || this.options.length === 0) {
      return '';
    }
    
    const selectedOption = this.options.find(opt => opt[this.valueKey] === this.value);
    if (selectedOption) {
      const display = selectedOption[this.displayKey];
      return typeof display === 'object' ? display[this.currentLanguage] : display;
    }
    return '';
  }

  private onChange: (value: any) => void = () => {};
  private onTouched: () => void = () => {};

  openModal(content: any) {
    if (this.disabled) {
      return;
    }
    
    this.searchTerm = '';
    this.filteredOptions = [...this.options];
    this.isFocused = true;
    
    this.modalRef = this.modalService.open(content, {
      centered: true,
      windowClass: 'custom-select-modal',
      backdrop: true,
      keyboard: true
    });

    // Emit opened event
    this.openedChange.emit(true);

    this.modalRef.result.then(
      () => {
        this.onModalClosed();
      },
      () => {
        this.onModalClosed();
      }
    );
  }

  onModalClosed() {
    this.isFocused = false;
    this.searchTerm = '';
    this.filteredOptions = [];
    this.closed.emit();
    this.openedChange.emit(false);
  }

  onSearchInput() {
    // Use debounced search
    this.searchSubject.next(this.searchTerm);
  }

  filterOptions() {
    if (!this.searchTerm || this.searchTerm.trim() === '') {
      this.filteredOptions = [...this.options];
      return;
    }

    const searchLower = this.searchTerm.toLowerCase().trim();
    this.filteredOptions = this.options.filter(option => {
      const display = option[this.displayKey];
      const displayText = typeof display === 'object' ? display[this.currentLanguage] : display;
      return displayText?.toLowerCase().includes(searchLower);
    });
  }

  onListScroll(event: any) {
    const element = event.target;
    const threshold = 1; // px from the bottom to trigger
    const position = element.scrollTop + element.clientHeight;
    const height = element.scrollHeight;

    if (position > height - threshold) {
      // Emit scroll event for infinite loading
      this.scrollEnd.emit();
    }
  }

  selectOption(option: any, modal: any) {
    this._value = option[this.valueKey];
    this.onChange(this._value);
    this.onTouched();
    this.selectChange.emit(this._value);
    this.cdRef.detectChanges();
    modal.close();
  }

  getOptionDisplayText(option: any): string {
    const display = option[this.displayKey];
    return typeof display === 'object' ? display[this.currentLanguage] : display;
  }

  trackByOption = (index: number, option: any): any => option[this.valueKey];

  // Touch/swipe handling for mobile
  private touchStartY: number = 0;
  private touchCurrentY: number = 0;

  onTouchStart(event: TouchEvent): void {
    this.touchStartY = event.touches[0].clientY;
  }

  onTouchMove(event: TouchEvent): void {
    this.touchCurrentY = event.touches[0].clientY;
  }

  onTouchEnd(event: TouchEvent, modal: any): void {
    const deltaY = this.touchCurrentY - this.touchStartY;
    // If swiped down more than 100px, close the modal
    if (deltaY > 100) {
      modal.dismiss();
    }
    this.touchStartY = 0;
    this.touchCurrentY = 0;
  }

  writeValue(value: any): void {
    if (value !== this._value) {
      this._value = value ?? null;
      this.cdRef.detectChanges();
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}

